import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Title, TextInput, Button, Text } from '@tremor/react';
import { login } from '../api/auth';
import { t } from '../lib/i18n';
import { EyeIcon, EyeOffIcon } from '../components/icons';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Remote-access gateway (/acceso) lands here as /login?u=<username> so the
  // user only types the password. Read it once, then drop it from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const u = searchParams.get('u');
    if (!u) return;
    setUsername((cur) => cur || u);
    const next = new URLSearchParams(searchParams);
    next.delete('u');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect Caps Lock state on key events targeting the password field. The
  // browser-native getModifierState reports its current state regardless of
  // which key triggered the event — so we update on every keydown/keyup so
  // the indicator flips the moment Caps Lock toggles.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Only listen when the focused element is the password input.
      const t = e.target as HTMLElement | null;
      if (!t || (t as HTMLInputElement).type !== 'password' && t.id !== 'password-input') return;
      setCapsLock(e.getModifierState && e.getModifierState('CapsLock'));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const user = await login(username.trim(), password);
      if (!user) {
        setError(t.login.invalid);
        setSubmitting(false);
        return;
      }
      navigate('/');
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-sm w-full">
        <Title className="mb-4">{t.appName}</Title>
        <Text className="mb-6">{t.login.title}</Text>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-tremor-default text-tremor-content-emphasis mb-1 block">
              {t.login.username}
            </label>
            <TextInput value={username} onValueChange={setUsername} autoFocus />
          </div>
          <div>
            <label className="text-tremor-default text-tremor-content-emphasis mb-1 block flex items-center">
              {t.login.password}
              {capsLock && (
                <span className="ml-2 text-amber-700 dark:text-amber-400 text-xs font-normal">
                  ⚠ Bloq Mayús activado
                </span>
              )}
            </label>
            {/* The show-password toggle replaces the TextInput component with
                a bare input briefly. Tremor's TextInput doesn't expose a
                way to drop the password mask, so we render either text or
                password input with consistent styling. The eye toggle sits
                inside the input via absolute positioning. */}
            <div className="relative">
              <input
                id="password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-tremor-default border border-tremor-border bg-tremor-background px-3 py-2 pr-10 text-tremor-default text-tremor-content-emphasis focus:outline-none focus:ring-2 focus:ring-tremor-brand"
                aria-label={t.login.password}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-tremor-content hover:text-tremor-content-strong text-base"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
          {error && <Text className="text-red-600 dark:text-red-400">{error}</Text>}
          <Button type="submit" className="w-full" loading={submitting} disabled={submitting}>
            {submitting ? t.login.signingIn : t.login.submit}
          </Button>
        </form>
      </Card>
    </div>
  );
}
